import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { ApiError } from '@/lib/api';
import {
  addSubscriptionLine,
  updateSubscriptionLine,
  verifySubscriptionLine,
  addCoveredAccount,
  removeCoveredAccount,
} from '../api';
import type { SubscriptionLineInput, SubscriptionLineUpdate } from '../lineTypes';
import { subscriptionKeys } from './queryKeys';

/**
 * The server's own message (e.g. 409 COVERED_HAS_SENSORS / COVERED_SELF) beats
 * the generic per-status text ApiError carries.
 */
export function serverErrorMessage(error: unknown): string {
  if (error instanceof ApiError && error.data && typeof error.data === 'object') {
    const body = error.data as { error?: unknown; message?: unknown; msg?: unknown };
    const msg = body.error ?? body.message ?? body.msg;
    if (typeof msg === 'string' && msg) return msg;
  }
  if (error instanceof Error) return error.message;
  return 'An unexpected error occurred';
}

// Lines and covered accounts arrive on GET /v4/subscriptions/:id, so every write
// refreshes that detail; the renewal panels read lines too.
function useLinesMutation<V>(subscriptionId: string, fn: (v: V) => Promise<unknown>, successKey: string) {
  const { t } = useTranslation('subscriptions');
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.detail(subscriptionId) });
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.renewalSummary(subscriptionId) });
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.renewalQuotePreview(subscriptionId) });
      toast.success(t(successKey));
    },
    onError: (error) => {
      toast.error(t('lines.toast.error'), { description: serverErrorMessage(error) });
    },
  });
}

export function useAddSubscriptionLine(subscriptionId: string) {
  return useLinesMutation(subscriptionId, (data: SubscriptionLineInput) => addSubscriptionLine(subscriptionId, data), 'lines.toast.added');
}

export function useUpdateSubscriptionLine(subscriptionId: string) {
  return useLinesMutation(
    subscriptionId,
    ({ lineId, data }: { lineId: string; data: SubscriptionLineUpdate }) => updateSubscriptionLine(subscriptionId, lineId, data),
    'lines.toast.updated',
  );
}

export function useEndSubscriptionLine(subscriptionId: string) {
  return useLinesMutation(
    subscriptionId,
    ({ lineId, endedAt }: { lineId: string; endedAt: string }) => updateSubscriptionLine(subscriptionId, lineId, { endedAt }),
    'lines.toast.ended',
  );
}

export function useVerifySubscriptionLine(subscriptionId: string) {
  return useLinesMutation(subscriptionId, (lineId: string) => verifySubscriptionLine(subscriptionId, lineId), 'lines.toast.verified');
}

export function useAddCoveredAccount(subscriptionId: string) {
  return useLinesMutation(
    subscriptionId,
    ({ accountId, reason }: { accountId: string; reason: string }) => addCoveredAccount(subscriptionId, accountId, reason),
    'lines.toast.coveredAdded',
  );
}

export function useRemoveCoveredAccount(subscriptionId: string) {
  return useLinesMutation(subscriptionId, (accountId: string) => removeCoveredAccount(subscriptionId, accountId), 'lines.toast.coveredRemoved');
}
